import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";

import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
const { Users, UserPlus, Search, Shield, ShieldCheck, ShieldOff, Eye,
  RefreshCw, Pause, Play, Power, Trash2, KeyRound, Edit3, Activity, X } = LucideIcons;
const SettingsIcon = LucideIcons.Settings;

const ROLES = ["owner", "editor", "viewer"];
const STATUSES = ["active", "suspended", "deactivated"];

const formatDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const initials = (name) => (name || "?").trim().slice(0, 2).toUpperCase();

const ROLE_BADGE = {
  owner: { label: "Owner", cls: "bg-[#1C3A5E] text-white" },
  editor: { label: "Editor", cls: "bg-[#34C759]/15 text-[#1f8e3a]" },
  viewer: { label: "Viewer", cls: "bg-[#FF9F0A]/15 text-[#c47600]" },
};
const STATUS_BADGE = {
  active: { label: "Active", cls: "bg-[#34C759]/15 text-[#1f8e3a]" },
  suspended: { label: "Suspended", cls: "bg-[#FF3B30]/15 text-[#c1271d]" },
  deactivated: { label: "Deactivated", cls: "bg-[#86868B]/20 text-[#86868B]" },
};

export default function Admin() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSupportGrant, setActiveSupportGrant] = useState(null);
  const [settings, setSettings] = useState({});
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [modal, setModal] = useState(null); // {type, user}

  const reloadUsers = async () => {
    const s = await api.get("/admin/users/summary");
    setSummary(s.data);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (roleFilter) params.set("role", roleFilter);
    if (statusFilter) params.set("status", statusFilter);
    const u = await api.get(`/admin/users?${params}`);
    setUsers(u.data);
  };
  const reloadActivity = async () => {
    const r = await api.get("/admin/activity?limit=50");
    setActivity(r.data);
  };
  const reloadSessions = async () => {
    try {
      const [sessRes, suppRes] = await Promise.all([
        api.get("/admin/sessions"),
        api.get("/support-access/current").catch(() => ({ data: {} })),
      ]);
      setSessions(sessRes.data || []);
      setActiveSupportGrant(suppRes.data?.active_grant || null);
    } catch (e) {
      // ignore
    }
  };
  const revokeSupportGrant = async (reqId) => {
    try {
      await api.post(`/support-access/requests/${reqId}/revoke`);
      toast.success("Support access revoked immediately");
      reloadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke access");
    }
  };
  const reloadSettings = async () => {
    const r = await api.get("/admin/settings");
    setSettings(r.data);
  };

  useEffect(() => {
    if (tab === "users") reloadUsers();
    if (tab === "activity") reloadActivity();
    if (tab === "sessions") reloadSessions();
    if (tab === "policies" || tab === "system") reloadSettings();
  // eslint-disable-next-line
  }, [tab, q, roleFilter, statusFilter]);

  const isSelf = (u) => u.id === me?.id;

  return (
    <Layout
      title="Admin Portal"
      subtitle="Users · Activity · Sessions · Company Policies"
      actions={
        <button onClick={() => navigate("/")} data-testid="back-to-app" className="p-2 rounded-full text-[#86868B] hover:bg-[#F5F5F7]" title="Back">
          <X size={18} />
        </button>
      }
    >
      <div className="segmented mb-6" data-testid="admin-tabs">
        <button className={tab === "users" ? "active" : ""} onClick={() => setTab("users")} data-testid="tab-users"><Users size={14} className="inline mr-1.5 -mt-0.5" />Users</button>
        <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")} data-testid="tab-activity"><Activity size={14} className="inline mr-1.5 -mt-0.5" />Activity</button>
        <button className={tab === "sessions" ? "active" : ""} onClick={() => setTab("sessions")} data-testid="tab-sessions"><Shield size={14} className="inline mr-1.5 -mt-0.5" />Sessions</button>
        <button className={tab === "policies" || tab === "system" ? "active" : ""} onClick={() => setTab("policies")} data-testid="tab-policies"><SettingsIcon size={14} className="inline mr-1.5 -mt-0.5" />Company Policies</button>
      </div>

      {/* ============== USERS TAB ============== */}
      {tab === "users" && (
        <>
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="users-summary">
              <SummaryCard label="Total" value={summary.total} testId="summary-total" />
              <SummaryCard label="Active" value={summary.active} accent="text-[#1f8e3a]" testId="summary-active" />
              <SummaryCard label="Suspended" value={summary.suspended} accent="text-[#c1271d]" testId="summary-suspended" />
              <SummaryCard label="Deactivated" value={summary.deactivated} accent="text-[#86868B]" testId="summary-deactivated" />
            </div>
          )}

          <div className="card p-4 mb-4 flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" />
              <input
                className="lapen-input pl-9"
                placeholder="Search by name or email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="users-search-input"
              />
            </div>
            <select className="lapen-input w-auto" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="users-role-filter">
              <option value="">All roles</option>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="lapen-input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="users-status-filter">
              <option value="">All statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => setModal({ type: "new" })} className="btn-primary flex items-center gap-2" data-testid="add-user-button">
              <UserPlus size={16} /> Add User
            </button>
          </div>

          <div className="card overflow-hidden" data-testid="users-table">
            <div className="divide-y divide-[#E5E5EA]/60">
              {users.length === 0 && (
                <div className="p-8 text-center text-sm text-[#86868B]">No users match your filters.</div>
              )}
              {users.map((u) => (
                <div key={u.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`user-row-${u.id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1C3A5E] to-[#2A547A] text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {initials(u.username)}
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {u.username}
                      {isSelf(u) && <span className="text-[10px] uppercase tracking-wider text-[#86868B]">(you)</span>}
                    </div>
                    <div className="text-xs text-[#86868B]">{u.email || "—"}</div>
                  </div>
                  <span className={`tag ${ROLE_BADGE[u.role]?.cls}`} data-testid={`user-role-${u.id}`}>{ROLE_BADGE[u.role]?.label}</span>
                  <span className={`tag ${STATUS_BADGE[u.status]?.cls}`} data-testid={`user-status-${u.id}`}>{STATUS_BADGE[u.status]?.label}</span>
                  <div className="text-xs text-[#86868B] min-w-[140px]">
                    <div>Last: {formatDT(u.last_login)}</div>
                    <div>Logins: {u.login_count}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <IconBtn onClick={() => setModal({ type: "edit", user: u })} icon={Edit3} label="Edit" testId={`edit-user-${u.id}`} />
                    <IconBtn onClick={() => setModal({ type: "reset", user: u })} icon={KeyRound} label="Reset password" testId={`reset-user-${u.id}`} />
                    {u.status === "active" && !isSelf(u) && (
                      <IconBtn onClick={() => setModal({ type: "suspend", user: u })} icon={Pause} label="Suspend" warn testId={`suspend-user-${u.id}`} />
                    )}
                    {u.status === "suspended" && (
                      <IconBtn onClick={async () => { await api.post(`/admin/users/${u.id}/unsuspend`); toast.success("Unsuspended"); reloadUsers(); }} icon={Play} label="Unsuspend" testId={`unsuspend-user-${u.id}`} />
                    )}
                    {u.status !== "deactivated" && !isSelf(u) && (
                      <IconBtn onClick={async () => { if (window.confirm(`Deactivate ${u.username}?`)) { await api.post(`/admin/users/${u.id}/deactivate`); toast.success("Deactivated"); reloadUsers(); } }} icon={Power} label="Deactivate" warn testId={`deactivate-user-${u.id}`} />
                    )}
                    {u.status === "deactivated" && (
                      <IconBtn onClick={async () => { await api.post(`/admin/users/${u.id}/reactivate`); toast.success("Reactivated"); reloadUsers(); }} icon={RefreshCw} label="Reactivate" testId={`reactivate-user-${u.id}`} />
                    )}
                    {!isSelf(u) && (
                      <IconBtn onClick={() => setModal({ type: "delete", user: u })} icon={Trash2} label="Delete" danger testId={`delete-user-${u.id}`} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ============== ACTIVITY TAB ============== */}
      {tab === "activity" && (
        <div className="card overflow-hidden" data-testid="activity-list">
          <div className="p-4 border-b border-[#E5E5EA]/60 flex items-center justify-between">
            <div>
              <h3 className="font-bold">Recent Activity</h3>
              <p className="text-xs text-[#86868B]">Last 50 login events and admin actions</p>
            </div>
            <button onClick={reloadActivity} className="text-[#1C3A5E] hover:bg-[#1C3A5E]/10 p-2 rounded-full" data-testid="refresh-activity"><RefreshCw size={16} /></button>
          </div>
          <div className="divide-y divide-[#E5E5EA]/60 max-h-[600px] overflow-y-auto">
            {activity.length === 0 && <div className="p-8 text-center text-sm text-[#86868B]">No activity yet.</div>}
            {activity.map((a) => {
              const isFail = a.action.includes("failure");
              return (
                <div key={a.id} className="p-3 px-4 flex items-center gap-3 text-sm" data-testid={`activity-${a.id}`}>
                  <div className={`w-2 h-2 rounded-full ${isFail ? "bg-[#FF3B30]" : "bg-[#34C759]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{a.username || "—"} <span className="font-normal text-[#86868B]">{a.action.replace(/_/g, " ")}</span></div>
                    {a.notes && <div className="text-xs text-[#86868B] truncate">{a.notes}</div>}
                  </div>
                  <div className="text-xs text-[#86868B] text-right shrink-0">
                    <div>{formatDT(a.timestamp)}</div>
                    {a.ip_address && <div className="font-mono">{a.ip_address}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============== SESSIONS TAB ============== */}
      {tab === "sessions" && (
        <div className="space-y-4">
          {activeSupportGrant && (
            <div className="card p-4 border-2 border-amber-500/40 bg-amber-500/5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-500 flex items-center justify-center">
                    <Shield size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-sm text-amber-600 dark:text-amber-400">
                        Active Super Admin Support Session
                      </h4>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                        {activeSupportGrant.requested_role}
                      </span>
                    </div>
                    <p className="text-xs text-[#86868B] mt-0.5">
                      Admin: <strong className="text-[var(--text)]">@{activeSupportGrant.super_admin_username}</strong> · 
                      Authorized until: <strong>{formatDT(activeSupportGrant.expires_at)}</strong>
                    </p>
                    <p className="text-xs text-[#86868B] mt-0.5">
                      Reason: <em>"{activeSupportGrant.reason}"</em>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => revokeSupportGrant(activeSupportGrant.id)}
                  className="px-4 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-500 font-bold text-xs flex items-center gap-1.5 transition-colors"
                >
                  <Power size={14} />
                  <span>Revoke Access Now</span>
                </button>
              </div>
            </div>
          )}

          <div className="card overflow-hidden" data-testid="sessions-list">
            <div className="p-4 border-b border-[#E5E5EA]/60 flex items-center justify-between">
              <div>
                <h3 className="font-bold">Active Sessions</h3>
              <p className="text-xs text-[#86868B]">{sessions.length} signed-in session(s)</p>
            </div>
            <button
              onClick={async () => { if (window.confirm("Revoke ALL sessions including yours?")) { await api.post("/admin/sessions/revoke-all"); toast.success("All sessions revoked"); window.location.href = "/"; } }}
              className="text-[#c1271d] text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#FF3B30]/10"
              data-testid="revoke-all-sessions"
            >
              Revoke All
            </button>
          </div>
          <div className="divide-y divide-[#E5E5EA]/60">
            {sessions.map((s) => (
              <div key={s.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`session-${s.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#1C3A5E]/10 text-[#1C3A5E] flex items-center justify-center font-bold text-xs">
                  {initials(s.username)}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="font-semibold text-sm">{s.username}</div>
                  <div className="text-xs text-[#86868B] font-mono">{s.ip_address || "—"}</div>
                </div>
                <div className="text-xs text-[#86868B] min-w-[200px]">
                  <div>Started: {formatDT(s.created_at)}</div>
                  <div>Last active: {formatDT(s.last_active)}</div>
                </div>
                <button
                  onClick={async () => { await api.post(`/admin/sessions/${s.id}/revoke`); toast.success("Session revoked"); reloadSessions(); }}
                  className="text-[#c1271d] text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#FF3B30]/10"
                  data-testid={`revoke-session-${s.id}`}
                >
                  Revoke
                </button>
              </div>
            ))}
            {sessions.length === 0 && <div className="p-8 text-center text-sm text-[#86868B]">No active sessions.</div>}
          </div>
        </div>
      </div>
    )}

      {/* ============== COMPANY POLICIES TAB ============== */}
      {(tab === "policies" || tab === "system") && (
        <CompanyPolicies settings={settings} reload={reloadSettings} />
      )}

      {/* ============== MODALS ============== */}
      {modal?.type === "new" && <NewUserModal onClose={() => setModal(null)} onSaved={reloadUsers} />}
      {modal?.type === "edit" && <EditUserModal user={modal.user} onClose={() => setModal(null)} onSaved={reloadUsers} />}
      {modal?.type === "reset" && <ResetModal user={modal.user} onClose={() => setModal(null)} />}
      {modal?.type === "suspend" && <SuspendModal user={modal.user} onClose={() => setModal(null)} onSaved={reloadUsers} />}
      {modal?.type === "delete" && <DeleteModal user={modal.user} onClose={() => setModal(null)} onSaved={reloadUsers} />}
    </Layout>
  );
}

// ---------- subcomponents ----------

function SummaryCard({ label, value, accent, testId }) {
  return (
    <div className="card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-semibold">{label}</div>
      <div className={`text-2xl font-extrabold tracking-tighter mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function IconBtn({ onClick, icon: Icon, label, warn, danger, testId }) {
  const cls = danger
    ? "text-[#c1271d] hover:bg-[#FF3B30]/10"
    : warn
      ? "text-[#c47600] hover:bg-[#FF9F0A]/10"
      : "text-[#86868B] hover:bg-[#F5F5F7] hover:text-[#1C3A5E]";
  return (
    <button onClick={onClick} title={label} className={`p-2 rounded-full transition-colors ${cls}`} data-testid={testId}>
      <Icon size={16} />
    </button>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid={testId}>
      <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="text-[#86868B] hover:text-[#1D1D1F]" data-testid="modal-close"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function NewUserModal({ onClose, onSaved }) {
  const [f, setF] = useState({ username: "", email: "", password: "", confirm: "", role: "editor", notes: "", must_change_password: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Passwords don't match");
    if (f.password.length < 6) return toast.error("Password must be at least 6 chars");
    setBusy(true);
    try {
      await api.post("/admin/users", { ...f });
      toast.success("User created");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add new user" onClose={onClose} testId="new-user-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Username" value={f.username} onChange={(v) => setF({ ...f, username: v })} required testId="newuser-username" />
        <Input label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="newuser-email" />
        <Input label="Password" type="password" value={f.password} onChange={(v) => setF({ ...f, password: v })} required testId="newuser-password" />
        <Input label="Confirm password" type="password" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} required testId="newuser-confirm" />
        <div>
          <label className="label">Role</label>
          <select className="lapen-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="newuser-role">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Input label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testId="newuser-notes" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.must_change_password} onChange={(e) => setF({ ...f, must_change_password: e.target.checked })} data-testid="newuser-mustchange" />
          Require password change on first login
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="newuser-submit">{busy ? "Creating…" : "Create user"}</button>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [f, setF] = useState({ username: user.username, email: user.email || "", role: user.role, notes: user.notes || "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/users/${user.id}`, f);
      toast.success("User updated"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Edit ${user.username}`} onClose={onClose} testId="edit-user-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Username" value={f.username} onChange={(v) => setF({ ...f, username: v })} required testId="edituser-username" />
        <Input label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="edituser-email" />
        <div>
          <label className="label">Role</label>
          <select className="lapen-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="edituser-role">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Input label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testId="edituser-notes" />
        <button type="submit" className="btn-primary w-full" data-testid="edituser-submit">Save changes</button>
      </form>
    </Modal>
  );
}

function ResetModal({ user, onClose }) {
  const [p, setP] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (p.length < 6) return toast.error("Min 6 chars");
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: p });
      toast.success("Password reset — user must log in again");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Reset password for ${user.username}`} onClose={onClose} testId="reset-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="New password" type="password" value={p} onChange={setP} required testId="reset-newpw" />
        <button type="submit" className="btn-primary w-full" data-testid="reset-submit">Reset password</button>
      </form>
    </Modal>
  );
}

function SuspendModal({ user, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/admin/users/${user.id}/suspend`, { reason, until: until || null });
      toast.success("User suspended"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Suspend ${user.username}`} onClose={onClose} testId="suspend-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Reason (required)" value={reason} onChange={setReason} required testId="suspend-reason" />
        <Input label="Suspend until (optional, blank = indefinite)" type="date" value={until} onChange={setUntil} testId="suspend-until" />
        <p className="text-xs text-[#86868B]">User will be logged out immediately. They'll see the reason on next login attempt.</p>
        <button type="submit" className="btn-primary w-full bg-[#FF3B30]" data-testid="suspend-submit">Suspend account</button>
      </form>
    </Modal>
  );
}

function DeleteModal({ user, onClose, onSaved }) {
  const [confirm, setConfirm] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/users/${user.id}/delete`, { confirm_username: confirm });
      toast.success("User deleted"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Delete ${user.username}`} onClose={onClose} testId="delete-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-[#c1271d]">This permanently deletes the account. Type the username to confirm.</p>
        <Input label={`Type "${user.username}"`} value={confirm} onChange={setConfirm} required testId="delete-confirm" />
        <button type="submit" className="btn-primary w-full bg-[#FF3B30]" data-testid="delete-submit">Delete user</button>
      </form>
    </Modal>
  );
}

const TIMEZONES = (() => {
  try {
    if (typeof Intl !== "undefined" && Intl.supportedValuesOf) {
      const list = Intl.supportedValuesOf("timeZone");
      if (list && list.length) return list;
    }
  } catch {}
  return [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Phoenix", "America/Anchorage", "America/Honolulu", "America/Detroit",
    "America/Indiana/Indianapolis", "America/Boise", "America/Puerto_Rico",
    "UTC", "Europe/London", "Europe/Paris", "Asia/Tokyo", "Asia/Kolkata"
  ];
})();

function CompanyPolicies({ settings, reload }) {
  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  const [f, setF] = useState({
    app_timezone: detectedTz,
    viewers_can_export: false,
    require_password_change_first_login: false,
    show_smart_deductibility_prompts: true,
    tax_year: "2025",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setF({
      app_timezone: settings.app_timezone || detectedTz,
      viewers_can_export: settings.viewers_can_export === "true" || settings.viewers_can_export === true,
      require_password_change_first_login: settings.require_password_change_first_login === "true" || settings.require_password_change_first_login === true,
      show_smart_deductibility_prompts: settings.show_smart_deductibility_prompts === "true" || settings.show_smart_deductibility_prompts === true,
      tax_year: settings.tax_year || "2025",
    });
  }, [settings, detectedTz]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        app_timezone: f.app_timezone,
        viewers_can_export: f.viewers_can_export,
        require_password_change_first_login: f.require_password_change_first_login,
        show_smart_deductibility_prompts: f.show_smart_deductibility_prompts,
        tax_year: f.tax_year,
      });
      toast.success("Company policies updated successfully");
      reload();
    } catch {
      toast.error("Failed to save company policies");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-6 space-y-6" data-testid="company-policies" id="company-policies">
      <div>
        <h3 className="font-bold text-lg mb-1">Company Policies & Configuration</h3>
        <p className="text-xs text-[#86868B]">
          Administer company-wide operational policies, viewer privileges, security requirements, and local timezone.
        </p>
      </div>

      {/* Timezone & Reporting Tax Year */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[#F5F5F7] dark:bg-[var(--surface-subtle)] rounded-2xl border border-[var(--border)]">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">Company Timezone</label>
            <button
              type="button"
              onClick={() => setF({ ...f, app_timezone: detectedTz })}
              className="text-[11px] text-teal-500 hover:underline font-medium"
            >
              Auto-detect ({detectedTz})
            </button>
          </div>
          <select
            className="lapen-input text-xs"
            value={f.app_timezone}
            onChange={(e) => setF({ ...f, app_timezone: e.target.value })}
            data-testid="sys-app-timezone"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
          <p className="text-[11px] text-[var(--muted)] mt-1">Used for audit trails, rental ledger dates, and automated emails.</p>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)] block mb-1.5">Active Reporting Tax Year</label>
          <input
            type="number"
            className="lapen-input text-xs"
            value={f.tax_year || "2025"}
            onChange={(e) => setF({ ...f, tax_year: e.target.value })}
            data-testid="sys-tax-year"
          />
          <p className="text-[11px] text-[var(--muted)] mt-1">Default reporting tax year for Schedule E generation and depreciation packages.</p>
        </div>
      </div>

      {/* Policy Toggles */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--muted)]">Operational & Security Rules</h4>

        <label className="flex items-start justify-between p-4 bg-[#F5F5F7] dark:bg-[var(--surface-subtle)] rounded-2xl border border-[var(--border)] cursor-pointer hover:border-teal-500/30 transition-colors" data-testid="sys-viewers-export-row">
          <div className="pr-4">
            <span className="text-sm font-semibold block text-[var(--text)]">Allow Viewers to Export Reports</span>
            <span className="text-xs text-[var(--muted)] block mt-0.5">Permit read-only viewer accounts to download Schedule E PDFs, mileage summaries, and CSV data.</span>
          </div>
          <input
            type="checkbox"
            checked={!!f.viewers_can_export}
            onChange={(e) => setF({ ...f, viewers_can_export: e.target.checked })}
            data-testid="sys-viewers-export"
            className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 mt-0.5"
          />
        </label>

        <label className="flex items-start justify-between p-4 bg-[#F5F5F7] dark:bg-[var(--surface-subtle)] rounded-2xl border border-[var(--border)] cursor-pointer hover:border-teal-500/30 transition-colors" data-testid="sys-pwchange-row">
          <div className="pr-4">
            <span className="text-sm font-semibold block text-[var(--text)]">Require Password Change on First Login</span>
            <span className="text-xs text-[var(--muted)] block mt-0.5">Forces newly invited team members to choose a personal password upon their initial sign-in.</span>
          </div>
          <input
            type="checkbox"
            checked={!!f.require_password_change_first_login}
            onChange={(e) => setF({ ...f, require_password_change_first_login: e.target.checked })}
            data-testid="sys-pwchange"
            className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 mt-0.5"
          />
        </label>

        <label className="flex items-start justify-between p-4 bg-[#F5F5F7] dark:bg-[var(--surface-subtle)] rounded-2xl border border-[var(--border)] cursor-pointer hover:border-teal-500/30 transition-colors" data-testid="sys-smart-prompts-row">
          <div className="pr-4">
            <span className="text-sm font-semibold block text-[var(--text)]">Enable Smart Deductibility Prompts</span>
            <span className="text-xs text-[var(--muted)] block mt-0.5">Display contextual IRS guidance and clarifying questions for ambiguous tax categories (Meals, Travel, Repairs).</span>
          </div>
          <input
            type="checkbox"
            checked={!!f.show_smart_deductibility_prompts}
            onChange={(e) => setF({ ...f, show_smart_deductibility_prompts: e.target.checked })}
            data-testid="sys-smart-prompts"
            className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 mt-0.5"
          />
        </label>
      </div>

      {/* Centralized Platform Services Card (Read-Only) */}
      <div className="p-4 bg-[var(--surface-subtle)] rounded-2xl border border-[var(--border)] space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] flex items-center gap-1.5">
            <LucideIcons.ShieldCheck size={14} className="text-teal-400" />
            <span>Platform-Managed Infrastructure & Statutory Rates</span>
          </h4>
          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400 border border-slate-500/20">
            Super Admin Governed
          </span>
        </div>
        <p className="text-xs text-[var(--muted)]">
          Core AI models, mail infrastructure, and IRS statutory deduction rates are maintained centrally by the system administrator to guarantee compliance and platform stability across all accounts.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <div className="p-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--text)] flex items-center gap-1">
                <LucideIcons.Sparkles size={13} className="text-teal-400" /> AI OCR Engine
              </span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                settings.platform_gemini_configured ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
              }`}>
                {settings.platform_gemini_configured ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="text-[11px] text-[var(--muted)]">Gemini Flash document parsing & receipt scanning.</p>
          </div>

          <div className="p-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--text)] flex items-center gap-1">
                <LucideIcons.Mail size={13} className="text-teal-400" /> Outgoing Mail
              </span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                settings.platform_smtp_configured ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
              }`}>
                {settings.platform_smtp_configured ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="text-[11px] text-[var(--muted)]">Automated tenant reminders & overdue notifications.</p>
          </div>

          <div className="p-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--text)] flex items-center gap-1">
                <LucideIcons.Scale size={13} className="text-teal-400" /> IRS Standard Rates
              </span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">
                ${settings.irs_mileage_rate || "0.70"}/mi
              </span>
            </div>
            <p className="text-[11px] text-[var(--muted)]">Meals {settings.business_meals_deductible_pct || 50}% · Bonus Depr {settings.bonus_depreciation_pct || 40}%</p>
          </div>
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary w-full py-2.5 text-xs font-semibold flex items-center justify-center gap-2"
          data-testid="sys-save"
        >
          <LucideIcons.CheckCircle2 size={16} />
          <span>{saving ? "Saving Policies..." : "Save Company Policies"}</span>
        </button>
      </div>
    </div>
  );
}

const SystemSettings = CompanyPolicies;

function Input({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => {
          if (type === "number" && (value === 0 || value === "0")) {
            onChange("");
          } else {
            e.target.select?.();
          }
        }}
        onClick={(e) => {
          if (type === "number" && (value === 0 || value === "0")) {
            onChange("");
          }
        }}
        required={required}
        className="lapen-input"
        data-testid={testId}

      />
    </div>
  );
}
